import React, { useMemo, useRef, useState } from "react";
import { SlidersHorizontal, X, MapPin } from "lucide-react";

/**
 * Suburb search like realestate.com.au (Christian Gravias, Monday 13202980753,
 * 17 Oct 2026): type a suburb, pick it from the list, and it becomes a chip.
 * Pick as many as you like. Anything typed that is not a suburb (an agency,
 * a street) is searched as text when you press Enter or Search.
 *
 * `options` is the API's suburb_options: [{ suburb, postcode, count }].
 * `selected` is a list of suburb names.
 */

const inputStyle = {
  background: "var(--c-panel-input)",
  backdropFilter: "blur(3.98905px)",
  boxShadow: "0px 0.99px 6px 0px #0000000F, 0px 0px 4px 0px #FFFFFF inset",
  border: "1px solid var(--c-line-input)",
};

const MAX_SUGGESTIONS = 8;

// Suburbs that start with what was typed come first, then ones that contain it,
// then postcode matches. Suburbs already picked are left out.
export const suggestSuburbs = (options, text, selected = []) => {
  const t = (text || "").trim().toLowerCase();
  if (!t) return [];
  const taken = new Set(selected.map((s) => s.toLowerCase()));
  const open = (options || []).filter((o) => o.suburb && !taken.has(o.suburb.toLowerCase()));
  const starts = open.filter((o) => o.suburb.toLowerCase().startsWith(t));
  const contains = open.filter((o) => !starts.includes(o) && o.suburb.toLowerCase().includes(t));
  const postcode = open.filter((o) => !starts.includes(o) && !contains.includes(o) && (o.postcode || "").startsWith(t));
  // Melbourne has two postcodes; one row per suburb name is enough.
  const seen = new Set();
  return [...starts, ...contains, ...postcode]
    .filter((o) => {
      const k = o.suburb.toLowerCase();
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    })
    .slice(0, MAX_SUGGESTIONS);
};

const SuburbChipSearch = ({
  options = [],
  selected = [],
  onChange,
  text,
  onTextChange,
  onSearch,
  onFilterClick,
  showFilters,
  activeFilterCount = 0,
  placeholder = "Search suburb, postcode, address or agency",
}) => {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);

  const suggestions = useMemo(() => suggestSuburbs(options, text, selected), [options, text, selected]);
  const countFor = useMemo(() => {
    const m = {};
    (options || []).forEach((o) => {
      const k = o.suburb.toLowerCase();
      m[k] = { postcode: m[k] ? m[k].postcode : o.postcode, count: (m[k] ? m[k].count : 0) + (o.count || 0) };
    });
    return m;
  }, [options]);

  const add = (name) => {
    onChange([...selected, name]);
    onTextChange("");
    onSearch("");
    setOpen(false);
    setActive(0);
    if (inputRef.current) inputRef.current.focus();
  };

  const remove = (name) => onChange(selected.filter((s) => s !== name));

  // Enter / Search: a highlighted or exactly typed suburb becomes a chip,
  // anything else is a text search.
  const commit = () => {
    const t = (text || "").trim();
    if (open && suggestions[active]) return add(suggestions[active].suburb);
    const exact = (options || []).find((o) => o.suburb.toLowerCase() === t.toLowerCase());
    if (exact && !selected.some((s) => s.toLowerCase() === t.toLowerCase())) return add(exact.suburb);
    setOpen(false);
    onSearch(t);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commit();
    } else if (e.key === "ArrowDown" && suggestions.length) {
      e.preventDefault();
      setOpen(true);
      setActive((i) => Math.min(suggestions.length - 1, i + 1));
    } else if (e.key === "ArrowUp" && suggestions.length) {
      e.preventDefault();
      setActive((i) => Math.max(0, i - 1));
    } else if (e.key === "Escape") {
      setOpen(false);
    } else if (e.key === "Backspace" && !text && selected.length) {
      remove(selected[selected.length - 1]);
    }
  };

  const filterButton = (mobile) => (
    <button
      type="button"
      onClick={onFilterClick}
      className={`relative flex items-center justify-center gap-2 rounded-full transition-all ${
        mobile ? "flex-1 py-3 rounded-2xl font-bold text-sm border" : "p-2.5 shadow-sm"
      } ${
        showFilters
          ? "bg-[#55C5D0] border-[#55C5D0] text-white"
          : activeFilterCount > 0
            ? "bg-white border-[#55C5D0] text-[#55C5D0] ring-1 ring-[#55C5D0]"
            : "bg-white border-gray-200 text-black hover:bg-gray-50"
      }`}
      aria-label={activeFilterCount > 0 ? `Toggle filters (${activeFilterCount} active)` : "Toggle filters"}
    >
      <SlidersHorizontal className="w-5 h-5" />
      {mobile && "Filters"}
      {activeFilterCount > 0 && !showFilters ? (
        <span
          className={`${mobile ? "ml-1" : "absolute -top-1 -right-1 ring-2 ring-white"} inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#55C5D0] text-white text-[11px] font-bold leading-none`}
        >
          {activeFilterCount}
        </span>
      ) : null}
    </button>
  );

  return (
    <div className="relative w-full">
      <div
        style={inputStyle}
        className="flex flex-col sm:flex-row sm:items-center gap-2 rounded-2xl sm:rounded-full py-2.5 sm:py-3 pl-3 sm:pl-6 pr-3 focus-within:border-[#55C5D0]"
        onClick={() => inputRef.current && inputRef.current.focus()}
      >
        <div className="flex flex-wrap items-center gap-1.5 flex-1 min-w-0">
          {selected.map((name) => (
            <span
              key={name}
              data-testid="suburb-chip"
              className="inline-flex items-center gap-1 rounded-full bg-[#55C5D0]/10 border border-[#55C5D0]/40 text-[#2b8f99] text-[13px] font-semibold pl-2.5 pr-1 py-1"
            >
              {name}
              {countFor[name.toLowerCase()]?.postcode ? (
                <span className="font-normal text-[#2b8f99]/70">{countFor[name.toLowerCase()].postcode}</span>
              ) : null}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  remove(name);
                }}
                className="p-0.5 rounded-full hover:bg-[#55C5D0]/20"
                aria-label={`Remove ${name}`}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </span>
          ))}
          <input
            ref={inputRef}
            type="text"
            value={text}
            onChange={(e) => {
              onTextChange(e.target.value);
              setOpen(true);
              setActive(0);
            }}
            onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 150)}
            onKeyDown={onKeyDown}
            placeholder={selected.length ? "Add another suburb" : placeholder}
            className="flex-1 min-w-[10rem] bg-transparent outline-none py-1.5 text-[15px] text-gray-600 placeholder:text-gray-400"
            aria-label="Search suburbs"
            role="combobox"
            aria-controls="suburb-suggestions"
            aria-expanded={open && suggestions.length > 0}
          />
        </div>
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          <span className="sm:hidden flex-1 flex">{filterButton(true)}</span>
          <span className="hidden sm:flex">{filterButton(false)}</span>
          <button
            type="button"
            onClick={commit}
            className="flex-[2] sm:flex-none bg-[#55C5D0] hover:bg-[#4ab3bd] text-white h-11 px-6 md:px-10 rounded-2xl sm:rounded-full font-bold text-sm transition-all shadow-md active:scale-95"
          >
            Search
          </button>
        </div>
      </div>

      {open && suggestions.length > 0 && (
        <ul
          id="suburb-suggestions"
          role="listbox"
          className="absolute z-30 left-0 right-0 sm:right-auto sm:min-w-[22rem] mt-1 bg-white border border-gray-200 rounded-xl shadow-lg py-1 max-h-80 overflow-auto"
        >
          {suggestions.map((o, i) => (
            <li
              key={`${o.suburb}-${o.postcode}`}
              role="option"
              aria-selected={i === active}
              onMouseDown={(e) => {
                e.preventDefault();
                add(o.suburb);
              }}
              onMouseEnter={() => setActive(i)}
              className={`flex items-center gap-2 px-3 py-2 cursor-pointer text-[13px] ${
                i === active ? "bg-[#55C5D0]/10" : ""
              }`}
            >
              <MapPin className="w-3.5 h-3.5 text-gray-400 shrink-0" />
              <span className="font-semibold text-gray-800">{o.suburb}</span>
              <span className="text-gray-400">VIC {o.postcode}</span>
              <span className="ml-auto text-[11px] text-gray-400">{countFor[o.suburb.toLowerCase()]?.count ?? o.count}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SuburbChipSearch;
